import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { AutoAwesome as SparklesIcon } from "@mui/icons-material";
import { useTranslationApp } from "../../../lib/i18next";
import {
  MODAL_CREATE_WITH_IA_GENERATED_CREATIVES,
  MODAL_CREATE_WITH_IA_GENERATIONS_IN_PROGRESS,
  MODAL_CREATE_WITH_IA_IMAGES_CREATED,
  MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY,
  MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY_NO_COUNT,
  MODAL_CREATE_WITH_IA_SAVING_TO_GALLERY,
  MODAL_CREATE_WITH_IA_MORE_VARIATIONS,
  BUTTON_SELECT_ALL,
  BUTTON_DESELECT_ALL,
  BUTTON_DOWNLOAD,
} from "../../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import Button from "../../Buttons/Button";
import Gallery from "../../Gallery/Gallery";
import ModalMedia from "../ModalMedia";
import ModalCanva from "../ModalCanva";
import ModalImageEdit from "../ModalImageEdit";
import ModalAnimateImagePreview from "../ModalAnimateImagePreview";
import { useAnimateImageWithPreview } from "../../../hooks/useAnimateImageWithPreview";
import { useDispatchApp } from "../../../lib/redux";
import { getPathDatabaseByRole } from "../../../actions/auth";
import { useContentModals } from "../../../contexts/contentModalsContext";
import {
  startResizeImage,
  startAnimateImage,
  startGetAwsDownloadUrl,
  startDownloadUrlsAsZip,
  startSubmitFeedback,
} from "../../../actions/user";
import {
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  GALLERY_BULK_INDIVIDUAL_DOWNLOAD_ZIP_THRESHOLD,
} from "../../../utils/constants";
import { useCreditActionCosts } from "../../../hooks/useCreditActionCosts";
import { getIsBlockedActions } from "../../../actions/getters";
import { getUniqueID } from "../../../utils/numbers";
import {
  buildResizeImagePayload,
  getResizingFormatItemPatch,
} from "../../../utils/buildResizeImagePayload";
import { extractResizeResultUrls } from "../../../utils/creativeJobResultUrls";
import IconButton from "../../Buttons/IconButton";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import GenerationPlaceholderCard from "./GenerationPlaceholderCard";
import GenerationFailedCard from "./GenerationFailedCard";
import SequentialGrid from "./SequentialGrid";
import {
  GENERATION_STATUS,
  isGenerationPlaceholder,
  stripGenerationPlaceholders,
} from "./utils/generationPlaceholders";

import { LaunchError } from "../../../utils/errors";

const COMMON_WIDTH_BUTTON = 180;

const getGeneratedMediaUrl = (media) =>
  typeof media === "string" ? media : media?.url || "";

const GeneratedImagesGallery = ({
  pendingGenerationsCount = 0,
  onRetryGeneration = () => {},
  onDismissGeneration = () => {},
  getPendingSpendForAction = () => 0,
  onReserveActionSpend = () => {},
  onReleaseActionSpend = () => {},
  onLocalMediaChanged = () => {},
  generatedMedia,
  ratings,
  highlightedIds = [],
  onGenerateMore,
  onChangeGeneratedMedia = () => {},
  onSelectMedia = () => {},
  onCallbackEditItem = () => {},
  onImportToGallery,
  onSelectAllMedia = () => {},
  selectedImages = [],
  multiple = true,
  mediaType,
  aspectRatio,
  numberOfImages,
  isTrial = false,
  onTrialExpandContent = () => {},
  onTrialDownloadContent = () => {},
  onClearTrialContentCTAPopup = () => {},
}) => {
  const { t } = useTranslationApp();
  const {
    ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
    ADS_CAMPAIGN_ANIMATE_IMAGE_ACTION,
  } = useCreditActionCosts();

  const { requestAnimateImage, previewModalProps } =
    useAnimateImageWithPreview();

  const [previewMediaUrl, setPreviewMediaUrl] = useState(null);
  const [selectedImageEditMediaData, setSelectedImageEditMediaData] =
    useState(null);
  const [selectedCanvaMediaData, setSelectedCanvaMediaData] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const isPreviewingMedia = previewMediaUrl !== null;
  const isOpenCanva = selectedCanvaMediaData !== null;
  const isOpenImageEditModal = selectedImageEditMediaData !== null;

  const hasGeneratedMedia = generatedMedia.length > 0;

  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const { openVideoEditor, close } = useContentModals();
  const videoEditorIdRef = useRef(null);
  const theme = useTheme();
  /** ≥1600px: layout título + botones en fila y botones con ancho fijo */
  const isXlg = useMediaQuery(theme.breakpoints.up(1600));

  // Función para agrupar imágenes por carouselID
  const groupedMedia = useMemo(() => {
    const carouselGroups = {};
    const individualItems = [];
    const carouselMap = new Map(); // Mapa de carouselID -> índices originales

    generatedMedia.forEach((media, index) => {
      // Caso 0: Item que ya viene agrupado como carousel (nueva estructura)
      if (media.type === "carousel" && Array.isArray(media.urls)) {
        const carouselID = media.carouselID || `generated-carousel-${index}`;

        // Crear estructura compatible para carouselItems
        carouselGroups[carouselID] = {
          carouselID,
          images: media.urls.map((url, imgIndex) => ({
            url,
            rating: ratings[url] || null,
            originalIndex: index, // Todos apuntan al mismo índice del objeto padre
            subIndex: imgIndex, // Nuevo: índice dentro del carousel
          })),
          originalIndices: [index], // El índice principal es el del objeto agrupado
          isGroupedObject: true, // Flag para identificar este tipo de estructura
        };
        return;
      }

      // Saltar items especiales (generating, resizing)
      if (typeof media === "object" && (media.status || media.resizingFormat)) {
        individualItems.push({ media, originalIndex: index });
        return;
      }

      const carouselID =
        typeof media === "object" && media.carouselID ? media.carouselID : null;

      if (carouselID) {
        if (!carouselGroups[carouselID]) {
          carouselGroups[carouselID] = {
            carouselID,
            images: [],
            originalIndices: [],
          };
        }
        const url = typeof media === "string" ? media : media.url;
        carouselGroups[carouselID].images.push({
          url,
          rating: ratings[url] || null,
          originalIndex: index,
        });
        carouselGroups[carouselID].originalIndices.push(index);
      } else {
        const url = typeof media === "string" ? media : media.url;
        individualItems.push({
          media: { url, rating: ratings[url] || null },
          originalIndex: index,
        });
      }
    });

    // Convertir grupos de carousel en items especiales
    const carouselItems = Object.values(carouselGroups).map((group) => ({
      isCarousel: true,
      carouselID: group.carouselID,
      carouselImages: group.images,
      originalIndices: group.originalIndices,
      url: group.images[0].url, // URL de la primera imagen para compatibilidad
      rating: group.images[0].rating,
      isGroupedObject: group.isGroupedObject,
    }));

    return {
      carouselItems,
      individualItems,
      carouselMap,
    };
  }, [generatedMedia, ratings]);

  // Combinar items individuales y carousels para Gallery
  // IMPORTANTE: preservar el orden cronológico de generatedMedia (más recientes primero)
  const galleryData = useMemo(() => {
    const data = [];
    const addedCarouselIds = new Set();

    generatedMedia.forEach((media, index) => {
      // Placeholders de resizing
      if (typeof media === "object" && media.resizingFormat) {
        data.push(media);
        return;
      }

      if (isGenerationPlaceholder(media)) {
        switch (media.status) {
          case GENERATION_STATUS.GENERATING:
            data.push({ ...media, isGenerating: true });
            return;
          case GENERATION_STATUS.FAILED:
            data.push({ ...media, isFailed: true });
            return;
          default: {
            const exhaustiveCheck = media.status;
            void exhaustiveCheck;
            return;
          }
        }
      }

      // Carousel nuevo (type: "carousel")
      if (media.type === "carousel" && Array.isArray(media.urls)) {
        const carouselID = media.carouselID || `generated-carousel-${index}`;
        const group = groupedMedia.carouselItems.find(
          (c) => c.carouselID === carouselID
        );
        if (group) {
          data.push({
            ...group,
            id: `carousel-${carouselID}`,
            stableKey: `carousel-${carouselID}`,
            isCarousel: true,
          });
        }
        return;
      }

      // Carousel legacy (solo agregar en la primera vez que vemos este carouselID)
      const carouselID =
        typeof media === "object" && media.carouselID ? media.carouselID : null;
      if (carouselID) {
        if (!addedCarouselIds.has(carouselID)) {
          addedCarouselIds.add(carouselID);
          const group = groupedMedia.carouselItems.find(
            (c) => c.carouselID === carouselID
          );
          if (group) {
            data.push({
              ...group,
              id: `carousel-${carouselID}`,
              isCarousel: true,
            });
          }
        }
        return;
      }

      // Imagen individual
      const url = typeof media === "string" ? media : media?.url;
      if (url) {
        data.push({
          url,
          id: index,
          stableKey: url,
          rating: ratings[url] || null,
          ...(typeof media === "object"
            ? {
                sourceUrl: media.sourceUrl || media.originalVideoUrl || "",
                subtitles: media.subtitles || null,
                duration: media.duration,
                name: media.name,
              }
            : {}),
        });
      }
    });

    return data;
  }, [generatedMedia, ratings, groupedMedia]);

  const selectedIndices = useMemo(() => {
    const indices = [];
    galleryData.forEach((item, galleryIndex) => {
      if (item.isCarousel && item.carouselImages) {
        // Check if all images of the carousel are selected
        const allSelected = item.carouselImages.every((img) =>
          selectedImages.includes(img.url)
        );
        if (allSelected) {
          indices.push(galleryIndex);
        }
      } else if (item.url) {
        if (selectedImages.includes(item.url)) {
          indices.push(galleryIndex);
        }
      }
    });
    return indices;
  }, [galleryData, selectedImages]);

  // Helper function to get original indices from carousel URLs
  const getCarouselOriginalIndices = useCallback((item, carouselUrls) => {
    return carouselUrls
      .map((url) => {
        const carouselImage = item.carouselImages.find(
          (img) => img.url === url
        );
        return carouselImage?.originalIndex;
      })
      .filter((index) => index !== undefined);
  }, []);

  // Helper function to handle grouped carousel selection
  const onGroupedCarouselSelection = useCallback(
    (item) => {
      const originalIndex = item.originalIndices?.[0];
      if (originalIndex !== undefined) {
        onSelectMedia(originalIndex);
      }
    },
    [onSelectMedia]
  );

  // Helper function to handle legacy carousel selection
  const onLegacyCarouselSelection = useCallback(
    (item, carouselUrls, allSelected) => {
      if (allSelected) {
        // Deselect: get all original indices and toggle them
        const originalIndices = getCarouselOriginalIndices(item, carouselUrls);
        onSelectMedia(originalIndices);
      } else {
        // Select: toggle each image individually
        carouselUrls.forEach((url) => {
          const carouselImage = item.carouselImages.find(
            (img) => img.url === url
          );
          if (carouselImage?.originalIndex !== undefined) {
            onSelectMedia(carouselImage.originalIndex);
          }
        });
      }
    },
    [onSelectMedia, getCarouselOriginalIndices]
  );

  // Helper function to handle carousel selection
  const onCarouselSelection = useCallback(
    (item) => {
      const carouselUrls = item.carouselImages.map((img) => img.url);
      const allSelected = carouselUrls.every((url) =>
        selectedImages.includes(url)
      );

      if (item.isGroupedObject) {
        onGroupedCarouselSelection(item);
      } else {
        onLegacyCarouselSelection(item, carouselUrls, allSelected);
      }
    },
    [selectedImages, onGroupedCarouselSelection, onLegacyCarouselSelection]
  );

  // Wrapper for onSelectMedia that handles carousels
  const onSelectMediaWrapper = useMemo(() => {
    return (galleryIndex) => {
      const item = galleryData[galleryIndex];
      if (!item) return;

      // Handle carousel items
      if (item.isCarousel && item.carouselImages) {
        onCarouselSelection(item);
        return;
      }

      // Handle individual items
      const originalIndex = item.id;
      if (originalIndex !== undefined) {
        onSelectMedia(originalIndex);
      }
    };
  }, [galleryData, onCarouselSelection, onSelectMedia]);

  const selectableMediaCount = useMemo(() => {
    return generatedMedia.filter((media) => {
      if (typeof media === "string") return true;
      return !media.status && !media.resizingFormat;
    }).length;
  }, [generatedMedia]);

  const allMediaSelected =
    hasGeneratedMedia &&
    selectableMediaCount > 0 &&
    selectedIndices.length === selectableMediaCount;

  // Helper function to get the correct index in generatedMedia from galleryData index
  const onGetGeneratedMediaIndex = useCallback(
    (galleryIndex, data) => {
      const galleryItem = galleryData[galleryIndex];
      if (!galleryItem) return null;

      // Handle carousel items
      if (
        galleryItem.isCarousel &&
        galleryItem.carouselImages &&
        data.isCarousel
      ) {
        const carouselIndexInGenerated = galleryItem.originalIndices?.[0];
        return carouselIndexInGenerated !== undefined
          ? carouselIndexInGenerated
          : null;
      }

      // Handle individual items
      return galleryItem.id ?? galleryIndex;
    },
    [galleryData]
  );

  // Helper function to handle carousel edit
  const onHandleCarouselEdit = useCallback(
    (galleryIndex, data, setStateFunction) => {
      const generatedIndex = onGetGeneratedMediaIndex(galleryIndex, data);
      if (generatedIndex !== null) {
        setStateFunction({
          ...data,
          index: generatedIndex,
          // carouselIndex ya viene en data desde GalleryItem
        });
        return true;
      }
      return false;
    },
    [onGetGeneratedMediaIndex]
  );

  // Helper function to handle individual edit
  const onHandleIndividualEdit = useCallback(
    (galleryIndex, data, setStateFunction) => {
      const generatedIndex = onGetGeneratedMediaIndex(galleryIndex, data);
      if (generatedIndex !== null) {
        setStateFunction({
          ...data,
          index: generatedIndex,
        });
      }
    },
    [onGetGeneratedMediaIndex]
  );

  // Handler for Canva edit
  const onEditWithCanvaHandler = useCallback(
    (data) => {
      const galleryIndex = data.index;
      const galleryItem = galleryData[galleryIndex];

      if (
        galleryItem?.isCarousel &&
        galleryItem?.carouselImages &&
        data.isCarousel
      ) {
        const handled = onHandleCarouselEdit(
          galleryIndex,
          data,
          setSelectedCanvaMediaData
        );
        if (handled) return;
      }

      onHandleIndividualEdit(galleryIndex, data, setSelectedCanvaMediaData);
    },
    [galleryData, onHandleCarouselEdit, onHandleIndividualEdit]
  );

  // Handler for IA edit
  const onEditWithIAHandler = useCallback(
    (data) => {
      const galleryIndex = data.index;
      const galleryItem = galleryData[galleryIndex];

      if (
        galleryItem?.isCarousel &&
        galleryItem?.carouselImages &&
        data.isCarousel
      ) {
        const handled = onHandleCarouselEdit(
          galleryIndex,
          data,
          setSelectedImageEditMediaData
        );
        if (handled) return;
      }

      onHandleIndividualEdit(galleryIndex, data, setSelectedImageEditMediaData);
    },
    [galleryData, onHandleCarouselEdit, onHandleIndividualEdit]
  );

  useEffect(() => {
    return () => {
      if (videoEditorIdRef.current) {
        close(videoEditorIdRef.current);
        videoEditorIdRef.current = null;
      }
    };
  }, [close]);

  const onEditWithVideoHandler = useCallback(
    (videoData) => {
      const generatedIndex = videoData.itemId;
      const videoEditorId = openVideoEditor({
        sourceUrl:
          videoData.sourceUrl || videoData.originalVideoUrl || videoData.url,
        subtitles: videoData.subtitles || null,
        sourceName: videoData.name || "video",
        pathStorage: `socialMedia/${userID}/`,
        persistToContentList: false,
        replaceExisting: true,
        onCallbackSave: (result) => {
          if (!result?.ok || !result.url) return;
          onCallbackEditItem(
            {
              url: result.url,
              sourceUrl: result.sourceUrl || result.item?.sourceUrl,
              subtitles: result.subtitles || result.item?.subtitles,
              duration: result.duration || result.item?.duration,
              mode: result.mode,
            },
            generatedIndex,
            { url: videoData.url }
          );
        },
        onCloseModal: () => {
          videoEditorIdRef.current = null;
        },
      });
      videoEditorIdRef.current = videoEditorId;
    },
    [onCallbackEditItem, openVideoEditor, userID]
  );

  const onPreviewMedia = (indexOrData) => {
    const isObjectData =
      typeof indexOrData === "object" && indexOrData !== null;
    const galleryIndex = isObjectData ? indexOrData.index : indexOrData;
    const carouselIndexFromData = isObjectData
      ? indexOrData.carouselIndex
      : undefined;
    const isFromCarousel = isObjectData ? indexOrData.isFromCarousel : false;

    const item = galleryData[galleryIndex];

    if (isTrial && item) {
      const trialItem =
        item.isCarousel && item.carouselImages
          ? { ...item, carouselImages: item.carouselImages, isCarousel: true }
          : item.url
            ? { url: item.url }
            : null;
      if (trialItem) {
        onTrialExpandContent(trialItem);
      }
    }

    if (item?.isCarousel && item.carouselImages) {
      const carouselIndexInGenerated = item.originalIndices?.[0];
      const carouselItem =
        carouselIndexInGenerated !== undefined
          ? generatedMedia[carouselIndexInGenerated]
          : null;

      const initialCarouselIndex =
        carouselIndexFromData !== undefined ? carouselIndexFromData : 0;
      const carouselImageUrl =
        item.carouselImages[initialCarouselIndex]?.url ||
        item.carouselImages[0]?.url;

      setPreviewMediaUrl({
        url: carouselImageUrl,
        index: carouselIndexInGenerated ?? galleryIndex,
        isCarousel: true,
        carouselImages: item.carouselImages,
        carouselIndex: initialCarouselIndex,
        itemId: carouselItem?.carouselID
          ? `carousel-${carouselItem.carouselID}`
          : undefined,
        isFromCarousel: isFromCarousel,
      });
    } else {
      const mediaItem = generatedMedia[item?.id || galleryIndex];
      const mediaUrl = getGeneratedMediaUrl(mediaItem) || item?.url;
      setPreviewMediaUrl({ url: mediaUrl, index: item?.id || galleryIndex });
    }
  };

  const onDownloadSelectedItems = async () => {
    const selectedUrls = selectedImages.filter(Boolean);
    if (selectedUrls.length === 0) return;

    if (isTrial) {
      onTrialDownloadContent(selectedUrls);
    }

    setIsDownloading(true);
    try {
      if (
        selectedUrls.length > GALLERY_BULK_INDIVIDUAL_DOWNLOAD_ZIP_THRESHOLD
      ) {
        await dispatch(
          startDownloadUrlsAsZip({
            urls: selectedUrls,
            fileName: "creatives.zip",
          })
        );
      } else {
        await dispatch(startGetAwsDownloadUrl(selectedUrls));
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const onCallbackResizeFormat = async ({
    index,
    format,
    formats,
    width,
    height,
    isCarousel,
    carouselUrls,
    showPlaceholder = false,
    onCreditsConfirmed,
  }) => {
    const resolvedFormats = Array.isArray(formats)
      ? formats
      : format
        ? [format]
        : [];
    const primaryFormat = resolvedFormats[0] || format;
    const isMultiFormat = resolvedFormats.length > 1;

    const mediaItem = generatedMedia[index];

    // Detectar si es carousel
    let isCarouselItem = isCarousel;
    let urlsToResize = [];

    if (isCarouselItem === undefined) {
      // Detectar carousel desde la estructura del mediaItem
      if (mediaItem?.type === "carousel" && Array.isArray(mediaItem.urls)) {
        isCarouselItem = true;
        urlsToResize = mediaItem.urls;
      } else if (mediaItem?.carouselID) {
        // Legacy: buscar todas las imágenes con el mismo carouselID
        const carouselImages = generatedMedia.filter((media) => {
          const mediaCarouselID =
            typeof media === "object" && media.carouselID
              ? media.carouselID
              : null;
          return mediaCarouselID === mediaItem.carouselID;
        });
        if (carouselImages.length > 1) {
          isCarouselItem = true;
          urlsToResize = carouselImages
            .map((media) => (typeof media === "string" ? media : media.url))
            .filter(Boolean);
        }
      }
    }

    if (!isCarouselItem) {
      // Imagen individual
      const url = typeof mediaItem === "string" ? mediaItem : mediaItem?.url;
      urlsToResize = url ? [url] : [];
    } else {
      // Usar carouselUrls si se proporciona, sino usar las detectadas
      urlsToResize = carouselUrls || urlsToResize;
    }

    if (urlsToResize.length === 0) {
      return;
    }

    const formatsCount = resolvedFormats.length || 1;

    // Calcular multiplicador de costo
    const multiplier =
      (isCarouselItem ? urlsToResize.length : 1) * formatsCount;

    // Verificar bloqueo de acciones ANTES de agregar placeholder (ÚNICA verificación de costo)
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
        multiplier: multiplier,
        showUsageConfirmationAlert: true,
        pendingSpend: getPendingSpendForAction(
          ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION
        ),
      })
    );
    if (isBlocked) {
      return { cancelled: true };
    }

    if (typeof onCreditsConfirmed === "function") {
      onCreditsConfirmed();
    }
    onLocalMediaChanged();

    const shouldUsePlaceholderItems =
      !isCarouselItem &&
      resolvedFormats.length > 0 &&
      (showPlaceholder || isMultiFormat);

    let placeholderIds = [];
    let carouselPlaceholderId = null;

    if (shouldUsePlaceholderItems) {
      placeholderIds = resolvedFormats.map(() => getUniqueID());
      onChangeGeneratedMedia((prev) => {
        const placeholders = resolvedFormats.map(
          (targetFormat, formatIndex) => ({
            ...getResizingFormatItemPatch({
              format: targetFormat,
              width,
              height,
            }),
            id: placeholderIds[formatIndex],
            url: placeholderIds[formatIndex],
          })
        );

        return [...placeholders, ...prev];
      });
    } else if (showPlaceholder && isCarouselItem) {
      carouselPlaceholderId = getUniqueID();
      onChangeGeneratedMedia((prev) => {
        const newData = [
          {
            ...getResizingFormatItemPatch({
              format: primaryFormat,
              width,
              height,
            }),
            id: carouselPlaceholderId,
            url: carouselPlaceholderId,
            isCarousel: true,
          },
          ...prev,
        ];
        return newData;
      });
    }

    const removePlaceholderItems = (items) => {
      const idsToRemove = new Set(
        [
          ...placeholderIds,
          ...(carouselPlaceholderId ? [carouselPlaceholderId] : []),
        ].filter(Boolean)
      );

      if (idsToRemove.size === 0) return items;

      return items.filter((item) => {
        if (typeof item !== "object" || item === null) return true;
        return !idsToRemove.has(item.id);
      });
    };

    const reservationID = getUniqueID();
    onReserveActionSpend({
      reservationID,
      action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
      multiplier,
    });

    try {
      const response = await dispatch(
        startResizeImage(
          buildResizeImagePayload({
            urls: urlsToResize,
            formats: resolvedFormats,
            format: primaryFormat,
            width,
            height,
          })
        )
      );

      if (response.ok) {
        const resizedUrls = extractResizeResultUrls(response);

        if (isCarouselItem) {
          // Para carousels, response.data es un array con todas las URLs redimensionadas
          if (resizedUrls.length > 0) {
            // Crear objeto carousel con las URLs redimensionadas
            // IMPORTANTE: SIEMPRE generar un carouselID ÚNICO para el carrusel redimensionado
            // para que no se agrupe con el carrusel original en groupedMedia
            const newCarouselID = `resized-carousel-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const carouselObject = {
              type: "carousel",
              urls: resizedUrls,
              carouselID: newCarouselID,
            };

            console.log(
              "Carrusel redimensionado con nuevo ID único:",
              newCarouselID,
              carouselObject
            );

            onChangeGeneratedMedia((prev) => {
              let updated = removePlaceholderItems(prev);
              const newMedia = [carouselObject, ...updated];
              console.log(
                "Estado actualizado - carruseles en generatedMedia:",
                newMedia.filter((m) => m.type === "carousel")
              );
              return newMedia;
            });
          }
        } else {
          const validUrls = resizedUrls.filter(Boolean);

          if (validUrls.length > 0) {
            onChangeGeneratedMedia((prev) => {
              const updated = removePlaceholderItems(prev);
              return [...validUrls, ...updated];
            });
          }
        }
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "removePlaceholderItems",
          capture: "exception",
        })
      );
      console.error("Error en resize:", error);
    } finally {
      onReleaseActionSpend({ reservationID });
      if (placeholderIds.length > 0 || carouselPlaceholderId) {
        onChangeGeneratedMedia((prev) => removePlaceholderItems(prev));
      }
    }

    return { success: true };
    // eslint-disable-next-line
  };

  const onResizeFromModal = async ({
    index,
    format,
    formats,
    width,
    height,
    isCarousel,
    carouselUrls,
    onCreditsConfirmed,
  }) => {
    const mediaItem = generatedMedia[index];

    // Detectar si es carousel
    let isCarouselItem = isCarousel;
    if (isCarouselItem === undefined) {
      if (mediaItem?.type === "carousel" && Array.isArray(mediaItem.urls)) {
        isCarouselItem = true;
      } else if (mediaItem?.carouselID) {
        const carouselImages = generatedMedia.filter((media) => {
          const mediaCarouselID =
            typeof media === "object" && media.carouselID
              ? media.carouselID
              : null;
          return mediaCarouselID === mediaItem.carouselID;
        });
        isCarouselItem = carouselImages.length > 1;
      }
    }

    setTimeout(() => {
      const galleryContainer = document.querySelector(
        '[data-testid="generated-images-gallery"]'
      );
      if (galleryContainer) {
        galleryContainer.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const cardContent = document.querySelector(".MuiCardContent-root");
        if (cardContent) {
          cardContent.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    }, 100);

    try {
      // onCallbackResizeFormat verifica el costo primero, y solo después de confirmar agrega el placeholder
      const result = await onCallbackResizeFormat({
        index,
        format,
        formats,
        width,
        height,
        isCarousel: isCarouselItem,
        carouselUrls,
        showPlaceholder: true, // Indicar que debe mostrar placeholder después de confirmar
        onCreditsConfirmed,
      });

      // Si el usuario canceló, no hacer nada más
      if (result?.cancelled) {
        return;
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onResizeFromModal",
          capture: "exception",
        })
      );
      console.error("Error en resize:", error);
    }
  };

  const onCallbackAnimateImage = async ({
    mediaUrl,
    prompt,
    outroVideoUrl,
  }) => {
    const generationDataBase = {
      mediaType,
      aspectRatio,
      numberOfImages,
      generatedMedia: stripGenerationPlaceholders(generatedMedia),
      ratings,
      isGenerating: false,
    };

    const response = await dispatch(
      startAnimateImage({
        imageUrl: mediaUrl,
        ...(prompt ? { prompt } : {}),
        ...(outroVideoUrl ? { outroVideoUrl } : {}),
        notificationContext: {
          source: "modalCreateWithIA",
          generationDataBase,
        },
      })
    );
    if (response?.ok && response?.data?.url) {
      const newUrl = response.data.url;
      onChangeGeneratedMedia((prev) => {
        return [newUrl, ...prev];
      });
    }
    return;
    // eslint-disable-next-line
  };

  const onAnimateFromModal = async ({ index }) => {
    const mediaUrl =
      typeof generatedMedia[index] === "string"
        ? generatedMedia[index]
        : generatedMedia[index]?.url;

    if (!mediaUrl) return;

    requestAnimateImage({
      imageUrl: mediaUrl,
      onConfirm: async ({
        prompt = "",
        outroVideoUrl = "",
        closePreview,
      } = {}) => {
        const isBlocked = await dispatch(
          getIsBlockedActions({
            action: ADS_CAMPAIGN_ANIMATE_IMAGE_ACTION,
            showUsageConfirmationAlert: true,
            pendingSpend: getPendingSpendForAction(
              ADS_CAMPAIGN_ANIMATE_IMAGE_ACTION
            ),
          })
        );
        if (isBlocked) {
          return;
        }
        closePreview?.();
        onLocalMediaChanged();

        setTimeout(() => {
          const galleryContainer = document.querySelector(
            '[data-testid="generated-images-gallery"]'
          );
          if (galleryContainer) {
            galleryContainer.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            const cardContent = document.querySelector(".MuiCardContent-root");
            if (cardContent) {
              cardContent.scrollTo({ top: 0, behavior: "smooth" });
            }
          }
        }, 100);

        const removeID = getUniqueID();
        const reservationID = getUniqueID();
        onReserveActionSpend({
          reservationID,
          action: ADS_CAMPAIGN_ANIMATE_IMAGE_ACTION,
          multiplier: 1,
        });

        onChangeGeneratedMedia((prev) => {
          const newData = [
            {
              resizingFormat: "animate",
              id: removeID,
              url: removeID,
            },
            ...prev,
          ];
          return newData;
        });

        try {
          await onCallbackAnimateImage({ mediaUrl, prompt, outroVideoUrl });
        } finally {
          onReleaseActionSpend({ reservationID });
          onChangeGeneratedMedia((prev) => {
            const newData = [...prev];
            return newData.filter((item) => item.id !== removeID);
          });
        }
      },
    });
  };

  const onRetry = useCallback(
    (generationID) => {
      onRetryGeneration({ generationID });
    },
    [onRetryGeneration]
  );

  const onDismiss = useCallback(
    (generationID) => {
      onDismissGeneration({ generationID });
    },
    [onDismissGeneration]
  );

  const itemGeneratingComponent = useMemo(
    () => ({ component: GenerationPlaceholderCard, props: {} }),
    []
  );

  const itemFailedComponent = useMemo(
    () => ({
      component: GenerationFailedCard,
      props: {
        onRetry,
        onDismiss,
      },
    }),
    [onDismiss, onRetry]
  );

  const realMediaCount = useMemo(
    () =>
      generatedMedia.filter((media) => {
        if (isGenerationPlaceholder(media)) return false;
        if (typeof media === "object" && media?.resizingFormat) return false;
        return true;
      }).length,
    [generatedMedia]
  );
  const hasRealMedia = realMediaCount > 0;

  // Handle feedback - send like/dislike directly to backend with URLs
  const onFeedback = useCallback(
    async (action, urls) => {
      if (!urls || !urls.length) return;
      await dispatch(startSubmitFeedback({ action, urls }));
    },
    [dispatch]
  );

  return (
    <Card
      data-testid="generated-images-gallery"
      sx={{
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderRadius: 2,
        mt: 1,
      }}
    >
      <CardHeader
        title={
          <Box
            sx={{
              display: "flex",
              flexDirection: isXlg ? "row" : "column",
              alignItems: isXlg ? "center" : "stretch",
              justifyContent: isXlg ? "space-between" : "flex-start",
              gap: isXlg ? 0 : 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "grey.100",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SparklesIcon sx={{ color: "text.primary", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: { xs: "20px", sm: "24px" },
                  }}
                >
                  {t(MODAL_CREATE_WITH_IA_GENERATED_CREATIVES)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "12px", sm: "14px" },
                  }}
                >
                  {hasRealMedia
                    ? t(MODAL_CREATE_WITH_IA_IMAGES_CREATED, {
                        count: realMediaCount,
                      })
                    : pendingGenerationsCount > 0
                      ? t(MODAL_CREATE_WITH_IA_GENERATIONS_IN_PROGRESS, {
                          count: pendingGenerationsCount,
                        })
                      : ""}
                  {hasRealMedia && pendingGenerationsCount > 0
                    ? ` · ${t(MODAL_CREATE_WITH_IA_GENERATIONS_IN_PROGRESS, {
                        count: pendingGenerationsCount,
                      })}`
                    : ""}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: isXlg ? "flex" : { xs: "flex", sm: "grid" },
                flexDirection: isXlg ? "row" : "column",
                gridTemplateColumns: { sm: "1fr 1fr" },
                gap: 1,
                justifyContent: isXlg ? "flex-end" : "flex-start",
                width: isXlg ? "auto" : { xs: "100%", lg: "auto" },
                mt: isXlg ? 0 : 2,
              }}
            >
              <Button
                color="secondary"
                onClick={onGenerateMore}
                sx={{ width: isXlg ? COMMON_WIDTH_BUTTON : "100%" }}
              >
                {t(MODAL_CREATE_WITH_IA_MORE_VARIATIONS)}
              </Button>
              <Button
                color="secondary"
                onClick={onSelectAllMedia}
                disabled={!hasRealMedia}
                sx={{ width: isXlg ? COMMON_WIDTH_BUTTON : "100%" }}
              >
                {allMediaSelected
                  ? t(BUTTON_DESELECT_ALL)
                  : t(BUTTON_SELECT_ALL)}
              </Button>
              <Button
                color="secondary"
                onClick={onDownloadSelectedItems}
                loading={isDownloading}
                disabled={isDownloading || selectedImages.length === 0}
                sx={{ width: isXlg ? COMMON_WIDTH_BUTTON : "100%" }}
              >
                {t(BUTTON_DOWNLOAD)} ({selectedImages.length})
              </Button>
              <Button
                color="primary"
                loading={isImporting}
                disabled={isImporting || selectedImages.length === 0}
                sx={{
                  width: isXlg ? COMMON_WIDTH_BUTTON : "100%",
                }}
                onClick={async () => {
                  if (onImportToGallery && !isImporting) {
                    setIsImporting(true);
                    try {
                      const selectedMediaUrls = selectedImages.filter(Boolean);
                      await onImportToGallery(selectedMediaUrls);
                    } finally {
                      setIsImporting(false);
                    }
                  }
                }}
              >
                {isImporting
                  ? t(MODAL_CREATE_WITH_IA_SAVING_TO_GALLERY)
                  : selectedImages.length === 0
                    ? t(MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY_NO_COUNT)
                    : t(MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY, {
                        count: selectedImages.length,
                      })}
              </Button>
            </Box>
          </Box>
        }
      />
      <CardContent>
        <Grid container spacing={2} sx={{ ml: 0, mr: 0 }}>
          <Gallery
            sxGallery={{
              pl: 0,
              pr: 0,
              width: "100%",
            }}
            showShimmer={true}
            data={galleryData}
            highlightedIds={highlightedIds}
            onSelectItem={onSelectMediaWrapper}
            onSelectPreview={onPreviewMedia}
            onEditWithCanva={onEditWithCanvaHandler}
            onEditWithIA={onEditWithIAHandler}
            onEditWithVideo={onEditWithVideoHandler}
            onDownloadItems={onTrialDownloadContent}
            selectedItems={selectedIndices}
            multiple={multiple}
            loadLastItem={true}
            showRating={true}
            showFiltersSelector={false}
            showResizeFormatButton={true}
            acceptedFormats={IMAGE_FORMATS.concat(VIDEO_FORMATS).concat(
              ADS_CAMPAIGN_CAROUSEL_FORMAT
            )}
            onCallbackResizeFormat={onResizeFromModal}
            onAnimateImage={onAnimateFromModal}
            showEmptyMessage={false}
            showDeleteButton={false}
            overlayPickerMode={false}
            showFeedbackButtons={true}
            onFeedback={onFeedback}
            showReportButton={true}
            itemGeneratingComponent={itemGeneratingComponent}
            itemFailedComponent={itemFailedComponent}
            LayoutComponent={SequentialGrid}
            columns={{ xs: 1, sm: 2, lg: 3, xl: hasGeneratedMedia ? 4 : 3 }}
          />
        </Grid>
      </CardContent>

      {isPreviewingMedia &&
        (() => {
          const itemForModal =
            previewMediaUrl.isCarousel && previewMediaUrl.carouselImages
              ? {
                  isCarousel: true,
                  carouselImages: previewMediaUrl.carouselImages,
                  id: previewMediaUrl.itemId,
                  url: previewMediaUrl.url,
                  _initialCarouselIndex:
                    previewMediaUrl.carouselIndex !== undefined
                      ? previewMediaUrl.carouselIndex
                      : 0,
                }
              : {
                  url: previewMediaUrl.url,
                };

          return (
            <ModalMedia
              modalOpen={isPreviewingMedia}
              onCloseModal={() => {
                setPreviewMediaUrl(null);
                onClearTrialContentCTAPopup();
              }}
              onEditWithCanva={(data) => {
                setSelectedCanvaMediaData({
                  ...data,
                  index: previewMediaUrl.index,
                  ...(previewMediaUrl.isCarousel && {
                    carouselIndex:
                      data.carouselIndex ?? previewMediaUrl.carouselIndex,
                    isCarousel: true,
                    itemId: data.itemId || previewMediaUrl.itemId,
                  }),
                });
              }}
              onEditWithIA={(data) => {
                setSelectedImageEditMediaData({
                  ...data,
                  index: previewMediaUrl.index,
                  ...(previewMediaUrl.isCarousel && {
                    carouselIndex:
                      data.carouselIndex ?? previewMediaUrl.carouselIndex,
                    isCarousel: true,
                    itemId: data.itemId || previewMediaUrl.itemId,
                  }),
                });
              }}
              onResizeFormat={({
                format,
                formats,
                width,
                height,
                onCreditsConfirmed,
              }) =>
                onResizeFromModal({
                  index: previewMediaUrl.index,
                  format,
                  formats,
                  width,
                  height,
                  onCreditsConfirmed,
                })
              }
              showEditButton={false}
              showDeleteButton={false}
              showShareButton={false}
              showDownloadButton={false}
              customActions={
                <IconButton
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    width: 40,
                    height: 40,
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                  onClick={async (e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onSelectMedia(previewMediaUrl.index);
                    setPreviewMediaUrl(null);
                  }}
                  type="icon"
                  loaderColor="white"
                >
                  {(() => {
                    const mediaUrl =
                      typeof previewMediaUrl.url === "string"
                        ? previewMediaUrl.url
                        : previewMediaUrl.url?.url || previewMediaUrl.url;
                    return selectedImages.includes(mediaUrl) ? (
                      <CloseIcon sx={{ color: "white", fill: "white" }} />
                    ) : (
                      <DoneIcon sx={{ color: "white", fill: "white" }} />
                    );
                  })()}
                </IconButton>
              }
              url={
                getGeneratedMediaUrl(previewMediaUrl.url) || previewMediaUrl.url
              }
              item={itemForModal}
              isFromCarousel={previewMediaUrl.isCarousel}
            />
          );
        })()}

      {isOpenCanva && (
        <ModalCanva
          modalOpen={isOpenCanva}
          onCloseModal={() => setSelectedCanvaMediaData(null)}
          onCallbackSave={(editedUrl) => {
            onCallbackEditItem(
              editedUrl,
              selectedCanvaMediaData.index,
              selectedCanvaMediaData
            );
            if (isPreviewingMedia) {
              setPreviewMediaUrl((prev) => ({
                ...prev,
                url: editedUrl,
              }));
            }
          }}
          imageUrl={selectedCanvaMediaData.url}
          imageDimensions={selectedCanvaMediaData.dimensions}
          pathStorage={`socialMedia/${userID}/`}
        />
      )}

      {isOpenImageEditModal && (
        <ModalImageEdit
          isOpen={isOpenImageEditModal}
          onClose={() => setSelectedImageEditMediaData(null)}
          initialImageUrl={selectedImageEditMediaData?.url}
          onCallbackSave={async (savePayload) => {
            onCallbackEditItem(
              savePayload,
              selectedImageEditMediaData.index,
              selectedImageEditMediaData
            );
          }}
        />
      )}

      <ModalAnimateImagePreview {...previewModalProps} />
    </Card>
  );
};

export default GeneratedImagesGallery;
